<template>
    <div class="about">
        <!-- 页面标题 -->
        <section class="page-header">
            <div class="container">
                <h1 class="page-title">关于我们</h1>
                <div class="breadcrumb">
                    <el-breadcrumb separator="/">
                        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
                        <el-breadcrumb-item>关于我们</el-breadcrumb-item>
                    </el-breadcrumb>
                </div>
            </div>
        </section>

        <!-- 组织架构 -->
        <section class="organization">
            <div class="container">
                <h2 class="section-title text-center">组织架构</h2>
                <div class="divider center"></div>
                <div class="org-chart">
                    <div class="org-level-2">
                        <div class="org-box">
                            <el-icon>
                                <User />
                            </el-icon>
                            <span>总经理</span>
                        </div>
                    </div>
                    <div class="org-level-3">
                        <div class="org-box" v-for="dept in departments" :key="dept.name">
                            <el-icon>
                                <component :is="dept.icon" />
                            </el-icon>
                            <span>{{ dept.name }}</span>
                            <div class="org-teams">
                                <div class="team-item" v-for="team in dept.teams" :key="team">
                                    {{ team }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 企业文化 -->
        <section class="culture">
            <div class="container">
                <h2 class="section-title text-center">企业文化</h2>
                <div class="divider center"></div>
                <div class="culture-grid">
                    <div class="culture-item" v-for="(item, index) in cultureList" :key="index">
                        <div class="culture-card" :style="{ backgroundImage: `url(${item.image})` }">
                            <div class="culture-content">
                                <el-icon :size="40">
                                    <component :is="item.icon" />
                                </el-icon>
                                <h3>{{ item.title }}</h3>
                                <p>{{ item.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 企业资质 -->
        <section class="credentials">
            <div class="container">
                <h2 class="section-title text-center">企业资质</h2>
                <div class="divider center"></div>
                <p class="credentials-intro">公开展示公司依法取得的主体登记与增值电信业务经营资质。</p>
                <div class="credentials-grid">
                    <router-link class="credential-item" to="/about/business-license">
                        <el-image src="/company-credentials/business-license.webp" fit="cover" />
                        <div class="credential-copy">
                            <h3>营业执照</h3>
                            <p>查看营业执照原件及完整登记信息</p>
                        </div>
                    </router-link>
                    <router-link class="credential-item" to="/about/telecom-license">
                        <el-image src="/company-credentials/telecom-license/page-01.webp" fit="cover" />
                        <div class="credential-copy">
                            <h3>增值电信业务经营许可证</h3>
                            <p>查看许可证信息、经营范围、完整 8 页证照及原始 PDF</p>
                        </div>
                    </router-link>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { User } from '@element-plus/icons-vue'

// 部门数据
const departments = [
    {
        name: '技术研发中心',
        icon: 'Monitor',
        teams: ['软件开发组', 'AI研发组', '测试运维组']
    },
    {
        name: '市场营销部',
        icon: 'Promotion',
        teams: ['市场策划组', '销售团队']
    },
    {
        name: '运营管理部',
        icon: 'OfficeBuilding',
        teams: ['项目管理组', '客户服务组']
    },
    {
        name: '人力资源部',
        icon: 'UserFilled',
        teams: ['招聘组', '培训发展组']
    }
]

// 企业文化数据
const cultureList = [
    {
        title: '以效益为中心',
        description: '追求企业与客户的双赢，创造持续稳定的经济效益，实现企业的可持续发展。',
        icon: 'TrendCharts',
        image: '/images/about/culture-1.jpg'
    },
    {
        title: '以市场为导向',
        description: '密切关注市场动态，快速响应客户需求，提供创新的解决方案。',
        icon: 'Aim',
        image: '/images/about/culture-2.jpg'
    },
    {
        title: '以人才为根本',
        description: '重视人才培养，为员工提供广阔的发展平台，实现个人与企业的共同成长。',
        icon: 'UserFilled',
        image: '/images/about/culture-3.jpg'
    },
    {
        title: '以业绩论英雄',
        description: '建立公平公正的评估体系，鼓励创新，奖励贡献，激发团队活力。',
        icon: 'Medal',
        image: '/images/about/culture-4.jpg'
    },
    {
        title: '积极承担社会责任',
        description: '注重环境保护，参与公益事业，为社会发展贡献力量。',
        icon: 'OfficeBuilding',
        image: '/images/about/culture-5.jpg'
    },
    {
        title: '追求创新发展',
        description: '持续推动技术创新和服务创新，引领行业发展，为客户创造更大价值。',
        icon: 'Light',
        image: '/images/about/culture-6.jpg'
    }
]

</script>

<style scoped lang="scss">
.about {
    padding-top: 80px;

    .page-header {
        background: linear-gradient(135deg, #1890ff 0%, #604FDF 100%);
        padding: 60px 0;
        color: #fff;
        margin-bottom: 60px;

        .page-title {
            font-size: 36px;
            margin-bottom: 10px;
        }

        .breadcrumb {
            :deep(.el-breadcrumb) {
                color: rgba(255, 255, 255, 0.8);

                .el-breadcrumb__item {
                    .el-breadcrumb__inner {
                        color: rgba(255, 255, 255, 0.8);

                        &:hover {
                            color: #fff;
                        }
                    }

                    &:last-child .el-breadcrumb__inner {
                        color: #fff;
                    }
                }
            }
        }
    }

    .section-title {
        font-size: 32px;
        color: #333;
        margin-bottom: 20px;
    }

    .divider {
        width: 60px;
        height: 3px;
        background: var(--el-color-primary);
        margin-bottom: 40px;

        &.center {
            margin-left: auto;
            margin-right: auto;
        }
    }

    // 组织架构样式
    .organization {
        padding: 60px 0;
        background: #fff;

        .org-chart {
            max-width: 1000px;
            margin: 0 auto;

            .org-level-2,
            .org-level-3 {
                display: flex;
                justify-content: center;
                flex-wrap: wrap;
                gap: 20px;
                margin-bottom: 40px;
                position: relative;

                &::after {
                    content: '';
                    position: absolute;
                    bottom: -20px;
                    left: 50%;
                    width: 2px;
                    height: 20px;
                    background: #ddd;
                }

                &:last-child::after {
                    display: none;
                }
            }

            .org-box {
                background: #fff;
                padding: 20px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                text-align: center;
                min-width: 200px;

                .el-icon {
                    font-size: 24px;
                    color: var(--el-color-primary);
                    margin-bottom: 10px;
                }

                span {
                    font-size: 16px;
                    font-weight: bold;
                    display: block;
                    margin-bottom: 10px;
                }

                .org-teams {
                    margin-top: 10px;

                    .team-item {
                        font-size: 14px;
                        color: #666;
                        padding: 4px 0;
                    }
                }
            }
        }
    }

    // 企业文化样式
    .culture {
        padding: 60px 0;
        background: #f8f9fa;

        .culture-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;

            .culture-item {

                &:first-child,
                &:last-child {
                    grid-column: span 1;
                }

                .culture-card {
                    height: 300px;
                    background-size: cover;
                    background-position: center;
                    border-radius: 12px;
                    overflow: hidden;
                    position: relative;
                    display: flex;
                    align-items: flex-end;
                    padding: 30px;

                    &::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2));
                        transition: all 0.3s;
                    }

                    &:hover::before {
                        background: rgba(0, 0, 0, 0.7);
                    }

                    .culture-content {
                        position: relative;
                        z-index: 1;
                        color: #fff;
                        width: 100%;
                        transition: all 0.3s;

                        .el-icon {
                            margin-bottom: 15px;
                            transform: translateY(20px);
                            opacity: 0;
                            transition: all 0.3s;
                        }

                        h3 {
                            font-size: 24px;
                            margin-bottom: 10px;
                            transform: translateY(0);
                            transition: all 0.3s;
                        }

                        p {
                            font-size: 16px;
                            line-height: 1.6;
                            opacity: 0;
                            transform: translateY(20px);
                            transition: all 0.3s;
                            margin: 0;
                        }
                    }

                    &:hover {
                        .culture-content {
                            transform: translateY(-20px);

                            .el-icon {
                                transform: translateY(0);
                                opacity: 1;
                            }

                            h3 {
                                transform: translateY(0);
                            }

                            p {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }
                    }
                }
            }
        }
    }

    // 企业资质样式
    .credentials {
        padding: 60px 0;
        background: #fff;

        .credentials-intro {
            margin: -18px 0 34px;
            color: #666;
            text-align: center;
        }

        .credentials-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 30px;
        }

        .credential-item {
            display: grid;
            grid-template-columns: 220px minmax(0, 1fr);
            gap: 24px;
            align-items: center;
            padding: 18px;
            color: inherit;
            text-decoration: none;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
            transition: transform 0.3s, box-shadow 0.3s;

            &:hover {
                transform: translateY(-6px);
                box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
            }

            .el-image {
                width: 220px;
                height: 160px;
                border-radius: 8px;
            }

            h3 {
                margin: 0 0 12px;
                color: #333;
                font-size: 20px;
            }

            p {
                margin: 0;
                color: #666;
                font-size: 14px;
                line-height: 1.7;
            }
        }
    }
}

// 响应式设计
@media (max-width: 992px) {
    .about {
        .culture .culture-grid {
            grid-template-columns: 1fr;

            .culture-item {

                &:first-child,
                &:last-child {
                    grid-column: span 1;
                }
            }
        }

        .credentials .credentials-grid { grid-template-columns: 1fr; }
    }
}

@media (max-width: 768px) {
    .about {
        .page-header {
            padding: 40px 0;

            .page-title {
                font-size: 28px;
            }
        }

        .organization .org-chart {
            .org-level-3 {
                flex-direction: column;
                align-items: center;
            }
        }

        .credentials .credential-item {
            grid-template-columns: 1fr;

            .el-image {
                width: 100%;
                height: 220px;
            }
        }
    }
}
</style>
